// Chamadas do Plano de ação (ações `pa_*` em /comercial/propostas-geradas).
// Contrato: docs/plano-acao.md (se divergir, vale o doc). Uma função por ação;
// o erro vem com a frase do servidor (`erroDe`), que é o que a tela mostra.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { normalizarLinha, type CamposBeachhead, type LinhaBeachhead } from './paginas/beachheadRegras';

async function post<T = any>(body: Record<string, any>, timeout = 30000): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token }, timeout });
  return (r.data?.body ? JSON.parse(r.data.body) : r.data) as T;
}

export const erroDe = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.erro || e?.message || 'erro desconhecido';

/** 409 = outra pessoa mexeu antes (revisão otimista). */
export const ehConflito = (e: any): boolean => e?.response?.status === 409;
/** 403 = sem `gestao.plano_acao.ver`. */
export const ehSemAcesso = (e: any): boolean => e?.response?.status === 403;

export type StatusEtapa = 'nao_iniciada' | 'em_andamento' | 'concluida' | 'travada';
export type StatusTarefa = 'pendente' | 'em_andamento' | 'concluida' | 'cancelada' | 'adiada';
export type StatusInfo = 'pendente' | 'em_andamento' | 'pronto';
export type FonteInfo = 'erp' | 'levantar' | 'decisao';

export interface Ciclo {
  id: number; titulo: string; consultoria: string | null; pergunta: string | null;
  inicio: string; fim: string; status: 'rascunho' | 'ligado' | 'encerrado'; modelo: string | null; revisao: number;
}
export type CicloResumo = Ciclo;
export interface Papel { papel: string; rotulo: string; email: string | null; nome: string | null }
export interface Tarefa {
  id: number; etapa_id: number; ordem: number; titulo: string; descricao: string | null;
  papel_responsavel: string | null; papeis_observadores: string[]; prazo: string | null;
  /** O andamento mora na própria tarefa (nada vai para Minhas atividades). */
  status: StatusTarefa; revisao: number;
  /** Tipo da página da tarefa: 'generica' (só texto, notas e links) ou uma página com dados do ERP. */
  pagina: string; pagina_config: Record<string, any>;
  concluida_em: string | null; concluida_por: string | null;
}
export interface Informacao {
  id: number; etapa_id: number | null; ordem: number; item: string; fonte: FonteInfo; onde: string | null;
  papel_responsavel: string | null; status: StatusInfo; link: string | null; observacao: string | null; revisao: number;
}
export interface Etapa {
  id: number; numero: number; titulo: string; inicio: string; fim: string;
  decisao_numero: number | null; decisao_titulo: string | null;
  o_que_fazer: string | null; como_fazer: string | null; entrega: string | null; quem_faz: string | null; tempo_estimado: string | null;
  papel_dono_decisao: string | null; status: StatusEtapa; motivo_trava: string | null;
  decisao_texto: string | null; decisao_em: string | null; decisao_por: string | null; revisao: number;
  tarefas: Tarefa[]; informacoes: Informacao[];
}
export interface Indicador {
  id: number; ordem: number; nome: string; como_mede: string | null; fonte: 'manual' | 'automatico';
  linha_base: string | null; linha_base_nota: string | null; meta: string | null; valor_atual: string | null;
  leitura_em: string | null; revisao: number;
}
export interface Marco { id: number; ordem: number; titulo: string; data_prevista: string | null; feito_em: string | null; observacao: string | null; revisao: number }
export interface Permissoes { pode_editar: boolean; pode_gerir: boolean; email: string; papeis_do_usuario: string[] }
export interface CicloCompleto {
  ciclo: Ciclo; papeis: Papel[]; etapas: Etapa[]; antes: Informacao[]; indicadores: Indicador[]; marcos: Marco[]; permissoes: Permissoes;
}
export interface Conta { email: string; nome: string }

const lista = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);

/**
 * O servidor pode mandar tarefas/informações aninhadas na etapa ou soltas no
 * topo (com `etapa_id`); a tela aceita os dois jeitos.
 */
export function normalizarCiclo(d: any): CicloCompleto {
  const soltasT = lista<Tarefa>(d?.tarefas);
  const soltasI = lista<Informacao>(d?.informacoes);
  const etapas = lista<Etapa>(d?.etapas).map((e) => ({
    ...e,
    tarefas: lista<Tarefa>(e.tarefas).concat(soltasT.filter((t) => t.etapa_id === e.id))
      .map((t) => ({
        ...t, papeis_observadores: lista<string>(t.papeis_observadores), status: t.status ?? 'pendente',
        pagina: t.pagina || 'generica', pagina_config: t.pagina_config && typeof t.pagina_config === 'object' ? t.pagina_config : {},
        concluida_em: t.concluida_em ?? null, concluida_por: t.concluida_por ?? null,
      }))
      .sort((a, b) => a.ordem - b.ordem),
    informacoes: lista<Informacao>(e.informacoes).concat(soltasI.filter((i) => i.etapa_id === e.id)).sort((a, b) => a.ordem - b.ordem),
  })).sort((a, b) => a.numero - b.numero);
  const antes = lista<Informacao>(d?.antes).concat(soltasI.filter((i) => i.etapa_id == null)).sort((a, b) => a.ordem - b.ordem);
  const p = d?.permissoes || {};
  return {
    ciclo: d.ciclo, papeis: lista<Papel>(d?.papeis), etapas, antes,
    indicadores: lista<Indicador>(d?.indicadores).sort((a, b) => a.ordem - b.ordem),
    marcos: lista<Marco>(d?.marcos).sort((a, b) => a.ordem - b.ordem),
    permissoes: {
      pode_editar: !!p.pode_editar, pode_gerir: !!p.pode_gerir, email: String(p.email || '').toLowerCase(),
      papeis_do_usuario: lista<string>(p.papeis_do_usuario),
    },
  };
}

/** `pode_gerir` vem de `permissoes` na listagem; se não vier, fica nulo (desconhecido) e a tela NÃO mostra botão de gestor. */
export async function listarCiclos(): Promise<{ ciclos: CicloResumo[]; pode_gerir: boolean | null }> {
  const d = await post({ action: 'pa_listar_ciclos' });
  const pg = d?.permissoes?.pode_gerir;
  return { ciclos: lista<CicloResumo>(d?.ciclos ?? d), pode_gerir: typeof pg === 'boolean' ? pg : null };
}
export async function obterCiclo(ciclo_id: number): Promise<CicloCompleto> {
  return normalizarCiclo(await post({ action: 'pa_obter_ciclo', ciclo_id }));
}
export const importarModelo = (modelo: string, inicio: string) => post({ action: 'pa_importar_modelo', modelo, inicio });
/** Resposta: `avisados` = quantas pessoas receberam o aviso no sino (a tarefa NÃO vira atividade). */
export const ligarCiclo = (ciclo_id: number) => post<{ avisados?: number }>({ action: 'pa_ligar_ciclo', ciclo_id });
export async function listarContas(): Promise<Conta[]> {
  const d = await post({ action: 'pa_contas' });
  return lista<Conta>(d?.contas ?? d);
}

// Escritas: sempre com a `revisao` que a tela leu (409 se mudou).
export const salvarEtapa = (id: number, revisao: number, campos: Partial<Pick<Etapa, 'status' | 'motivo_trava' | 'decisao_texto' | 'decisao_em'>>) =>
  post({ action: 'pa_salvar_etapa', id, revisao, ...campos });
/** O status é protegido pela `revisao` da própria tarefa (409 se outra pessoa mexeu antes). */
export const salvarTarefa = (id: number, revisao: number, campos: Partial<Pick<Tarefa, 'status' | 'titulo' | 'descricao' | 'prazo' | 'papel_responsavel'>>) =>
  post({ action: 'pa_salvar_tarefa', id, revisao, ...campos });
export const criarTarefa = (etapa_id: number, campos: Pick<Tarefa, 'titulo' | 'descricao' | 'prazo' | 'papel_responsavel'>) =>
  post({ action: 'pa_criar_tarefa', etapa_id, ...campos });
export const salvarInformacao = (id: number, revisao: number, campos: Partial<Pick<Informacao, 'status' | 'link' | 'observacao'>>) =>
  post({ action: 'pa_salvar_informacao', id, revisao, ...campos });
export const salvarIndicador = (id: number, revisao: number, campos: Partial<Pick<Indicador, 'meta' | 'valor_atual' | 'leitura_em'>>) =>
  post({ action: 'pa_salvar_indicador', id, revisao, ...campos });
export const salvarMarco = (id: number, revisao: number, campos: Partial<Pick<Marco, 'data_prevista' | 'feito_em' | 'observacao'>>) =>
  post({ action: 'pa_salvar_marco', id, revisao, ...campos });
export const salvarPapel = (ciclo_id: number, papel: string, email: string) =>
  post({ action: 'pa_salvar_papel', ciclo_id, papel, email });

// ─── Página da tarefa (ações pa_pagina_*, pa_nota_*, pa_link_salvar, pa_atualizar_do_modelo) ───
export interface PaginaInfo {
  tipo: string; fonte: string | null; config: Record<string, any>;
  /** Pode ver o conteúdo especial (permissão do tipo). `faltam` = códigos de permissão que não tem. */
  pode_ver: boolean; faltam: string[]; pode_editar: boolean;
}
export interface Nota { id: number; texto: string; por: string; por_nome?: string | null; em: string }
export interface LinkTarefa { id: number; titulo: string; url: string; por: string; por_nome?: string | null; em: string }
export interface PermissoesPagina {
  pode_mudar_status: boolean; pode_anotar: boolean;
  /** Opcionais: se o servidor não mandar, a tela só esconde "apagar" nota de outra pessoa. */
  pode_gerir?: boolean; email?: string;
}
export interface PaginaObtida {
  tarefa: Tarefa;
  etapa: { id: number; numero: number; titulo: string; inicio: string; fim: string };
  ciclo: { id: number; titulo: string; status: Ciclo['status'] };
  papeis: Papel[]; notas: Nota[]; links: LinkTarefa[];
  pagina: PaginaInfo; permissoes: PermissoesPagina;
}

export type Diagnostico = 'houve' | 'nao_houve' | 'dispensada';
/** Campos das anotações que ficam gravadas no próprio negócio (comercial.propostas). */
export interface CamposLinha {
  situacao_cliente: string | null; diagnostico: Diagnostico | null; diagnostico_data: string | null;
  diagnostico_motivo: string | null; motivo_ganho: string | null; motivo_perda: string | null;
}
export interface LinhaProposta extends CamposLinha {
  id: number; titulo: string; cliente: string | null; segmento: string | null; valor: number | null;
  status: 'ganho' | 'perdido'; data_envio: string | null; data_fechamento: string | null;
  responsavel_nome: string | null; canal: string | null;
  /** Sugestão: reuniões do negócio registradas antes do envio da proposta. */
  reunioes_antes: number; ultima_reuniao_antes: string | null;
}

export function normalizarPagina(d: any): PaginaObtida {
  const p = d?.pagina || {};
  const t = normalizarCiclo({ ciclo: d?.ciclo, etapas: [{ id: d?.tarefa?.etapa_id, numero: 0, tarefas: [d?.tarefa] }] }).etapas[0].tarefas[0];
  const pm = d?.permissoes || {};
  return {
    tarefa: t, etapa: d?.etapa, ciclo: d?.ciclo, papeis: lista<Papel>(d?.papeis),
    notas: lista<Nota>(d?.notas), links: lista<LinkTarefa>(d?.links),
    pagina: {
      tipo: p.tipo || t.pagina || 'generica', fonte: p.fonte ?? null, config: p.config || t.pagina_config || {},
      pode_ver: p.pode_ver !== false, faltam: lista<string>(p.faltam), pode_editar: !!p.pode_editar,
    },
    permissoes: {
      pode_mudar_status: !!pm.pode_mudar_status, pode_anotar: !!pm.pode_anotar,
      pode_gerir: typeof pm.pode_gerir === 'boolean' ? pm.pode_gerir : undefined,
      email: pm.email ? String(pm.email).toLowerCase() : undefined,
    },
  };
}

export async function obterPagina(tarefa_id: number): Promise<PaginaObtida> {
  return normalizarPagina(await post({ action: 'pa_pagina_obter', tarefa_id }));
}
/**
 * Linha como o SERVIDOR manda (planoAcaoPaginas.js, docs/plano-acao.md) -> linha como a tela usa.
 * Os nomes diferem de propósito (o servidor fala do negócio; a tela, da linha da tabela).
 */
export function normalizarLinhaProposta(r: any): LinhaProposta {
  const s = r?.sugestao_reuniao || {};
  return {
    id: Number(r?.chave ?? r?.negocio_id), titulo: r?.titulo ?? '', cliente: r?.cliente ?? null, segmento: r?.segmento ?? null,
    valor: r?.valor == null ? null : Number(r.valor),
    status: r?.resultado === 'ganho' ? 'ganho' : 'perdido',
    data_envio: r?.data_envio ?? null, data_fechamento: r?.data_decisao ?? null,
    responsavel_nome: r?.responsavel ?? null, canal: r?.canal_origem ?? null,
    situacao_cliente: r?.situacao_cliente ?? null, diagnostico: r?.diagnostico ?? null, diagnostico_data: r?.diagnostico_data ?? null,
    diagnostico_motivo: r?.diagnostico_motivo ?? null, motivo_ganho: r?.motivo_ganho ?? null, motivo_perda: r?.motivo_perda ?? null,
    reunioes_antes: Number(s.reunioes_antes) || 0, ultima_reuniao_antes: s.ultima_reuniao_antes ?? null,
  };
}

/** Conteúdo com dados do ERP (só tipos de fonte `comercial`). 403 = falta permissão do tipo. */
export async function conteudoPropostas(tarefa_id: number): Promise<LinhaProposta[]> {
  const d = await post({ action: 'pa_pagina_conteudo', tarefa_id });
  return lista<any>(d?.linhas).map(normalizarLinhaProposta);
}
/** `vistos` = valor que a tela leu de cada campo enviado; 409 se mudou desde então. Devolve a linha nova, se o servidor mandar. */
export async function salvarLinhaProposta(
  tarefa_id: number, chave: number, campos: Partial<CamposLinha>, vistos: Partial<CamposLinha>,
): Promise<LinhaProposta | null> {
  const d = await post({ action: 'pa_pagina_salvar_linha', tarefa_id, chave, campos, vistos });
  return d?.linha ? normalizarLinhaProposta(d.linha) : null;
}
/** Conteúdo da página "Segmentos × 4 filtros" (tipo `beachhead_filtros`): a tabela e os textos fixos da Bcorp. */
export interface ConteudoBeachhead {
  linhas: LinhaBeachhead[];
  definicoes: Record<string, string>;
  limiares_aceite: string;
  limite_motivo: number;
  /** Texto da IA que deu as sugestões (página "Onde a Hydrodata ganha"); nulo = ainda não houve. */
  ia: { retrato_id: number | null; concluido_em: string | null; pedido_por: string | null } | null;
}
export async function conteudoBeachhead(tarefa_id: number): Promise<ConteudoBeachhead> {
  const d = await post({ action: 'pa_pagina_conteudo', tarefa_id });
  const ia = d?.ia && typeof d.ia === 'object' ? d.ia : null;
  return {
    linhas: lista<any>(d?.linhas).map(normalizarLinha),
    definicoes: d?.definicoes && typeof d.definicoes === 'object' ? d.definicoes : {},
    limiares_aceite: String(d?.limiares_aceite ?? ''),
    limite_motivo: Number(d?.limite_motivo) > 0 ? Number(d.limite_motivo) : 300,
    ia: ia ? { retrato_id: ia.retrato_id ?? null, concluido_em: ia.concluido_em ?? null, pedido_por: ia.pedido_por ?? null } : null,
  };
}
/** `chave` = nome do segmento; `vistos.revisao` = a revisão que a tela leu (nula = linha ainda não gravada). Devolve a linha nova. */
export async function salvarLinhaBeachhead(
  tarefa_id: number, chave: string, campos: CamposBeachhead, vistos: { revisao: number | null },
): Promise<LinhaBeachhead | null> {
  const d = await post({ action: 'pa_pagina_salvar_linha', tarefa_id, chave, campos, vistos });
  return d?.linha ? normalizarLinha(d.linha) : null;
}
export const criarNota = (tarefa_id: number, texto: string) => post({ action: 'pa_nota_criar', tarefa_id, texto });
export const apagarNota = (tarefa_id: number, id: number) => post({ action: 'pa_nota_apagar', tarefa_id, id });
export const salvarLink = (tarefa_id: number, titulo: string, url: string) => post({ action: 'pa_link_salvar', tarefa_id, titulo, url });

/** Item da prévia: o servidor pode mandar texto puro ou um objeto com a frase pronta. */
export type ItemPrevia = string | { descricao?: string; texto?: string; mensagem?: string; [k: string]: any };
export interface PreviaModelo { mudancas: ItemPrevia[]; ignoradas: ItemPrevia[] }
export async function atualizarDoModelo(ciclo_id: number, aplicar: boolean): Promise<PreviaModelo> {
  const d = await post({ action: 'pa_atualizar_do_modelo', ciclo_id, aplicar }, 60000);
  return { mudancas: lista<ItemPrevia>(d?.mudancas), ignoradas: lista<ItemPrevia>(d?.ignoradas) };
}
